/**
 * Click-triggered popover for FullCalendar events.
 * Shows full title, status, metadata, and a link to the show page.
 */

let activePopover = null;

function closePopover() {
  if (activePopover) {
    activePopover.remove();
    activePopover = null;
  }
  document.removeEventListener("click", onDocumentClick, true);
}

function onDocumentClick(e) {
  if (activePopover && !activePopover.contains(e.target)) {
    closePopover();
  }
}

export function handleEventClick(info) {
  info.jsEvent.preventDefault();
  info.jsEvent.stopPropagation();

  const { event } = info;
  const props = event.extendedProps || {};

  // GR-3145: an org-wide blocked date renders as a background wash with nothing
  // to click, so it keeps the early return. A facility closure is a real all-day
  // item whose "<facility>: <reason>" title truncates in a day cell, so it needs
  // the pop-over to carry the reason — this guard used to skip both.
  if (props.type === "blocked_date" && !props.facility) return;

  // Close any existing popover
  closePopover();

  const popover = document.createElement("div");
  popover.className = "fc-event-popover";

  // Title
  const title = document.createElement("div");
  title.className = "fc-event-popover-title";
  // A closure's calendar label is "<facility>: <reason>" so it reads in a day
  // cell. In here the reason is a meta line of its own, so the title carries
  // the facility alone rather than repeating both halves.
  title.textContent =
    props.type === "blocked_date" && props.facility ? props.facility : event.title;
  popover.appendChild(title);

  // Meta lines
  const meta = document.createElement("div");
  meta.className = "fc-event-popover-meta";

  if (props.type === "event") {
    addMeta(meta, "Status", capitalize(props.status));
    if (props.registrationCount != null) {
      addMeta(meta, "Registrations", props.registrationCount);
    }
  } else if (props.type === "event_request") {
    addMeta(meta, "Status", humanize(props.status));
    if (props.requester) addMeta(meta, "Requester", props.requester);
  } else if (props.type === "facility_booking") {
    addMeta(meta, "Booking", humanize(props.bookingStatus));
    addMeta(meta, "Request", humanize(props.requestStatus));
    if (props.requester) addMeta(meta, "Requester", props.requester);
  } else if (props.type === "facility_schedule_entry") {
    if (props.venue) addMeta(meta, "Venue", props.venue);
    if (props.timeRange) addMeta(meta, "Time", props.timeRange);
    if (props.headcount != null) addMeta(meta, "Covers booked", props.headcount);
    if (props.quantity != null) addMeta(meta, "Quantity", props.quantity);
    // GR-3219: the two dietary facts are labelled by their source, because a
    // requester's typed note and what the attendees declared routinely
    // disagree and the kitchen needs to know which is which.
    addDietaryBreakdown(meta, props);
    if (props.dietaryNotes) addMeta(meta, "Requester's note", props.dietaryNotes);
    if (props.setupNotes) addMeta(meta, "Setup notes", props.setupNotes);
    addMeta(meta, "Booking", humanize(props.bookingStatus));
  } else if (props.type === "blocked_date") {
    addMeta(meta, "Status", "Closed");
    if (props.reason) addMeta(meta, "Reason", props.reason);
  }

  // GR-3080: every item type carries the lead when the originating request
  // named one, so staff can see who runs the event without opening it.
  // GR-4096: under the organization's own name for the role, which the
  // calendar serializers send alongside the lead.
  if (props.eventLead) addMeta(meta, props.eventLeadLabel || "Event Lead", props.eventLead);

  popover.appendChild(meta);

  // View link
  if (event.url) {
    const link = document.createElement("a");
    link.href = event.url;
    link.className = "fc-event-popover-link";
    link.textContent = "View →";
    link.addEventListener("click", (e) => {
      e.preventDefault();
      closePopover();
      if (window.Turbo) {
        window.Turbo.visit(event.url);
      } else {
        window.location = event.url;
      }
    });
    popover.appendChild(link);
  }

  // Position relative to clicked element
  document.body.appendChild(popover);
  positionPopover(popover, info.el);
  activePopover = popover;

  // Close on outside click (next tick so this click doesn't immediately close it)
  requestAnimationFrame(() => {
    document.addEventListener("click", onDocumentClick, true);
  });
}

function positionPopover(popover, anchorEl) {
  const rect = anchorEl.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();

  let top = rect.bottom + 4 + window.scrollY;
  let left = rect.left + window.scrollX;

  // Keep within viewport horizontally
  if (left + popoverRect.width > window.innerWidth - 8) {
    left = window.innerWidth - popoverRect.width - 8 + window.scrollX;
  }
  if (left < 8) left = 8;

  // Flip above if not enough room below
  if (rect.bottom + popoverRect.height + 8 > window.innerHeight) {
    top = rect.top - popoverRect.height - 4 + window.scrollY;
  }

  popover.style.top = `${top}px`;
  popover.style.left = `${left}px`;
}

// One labelled block for what the registered attendees declared: the count of
// people it was counted from, then a list with one choice per line. Nothing
// declared is said outright rather than left as an absence.
function addDietaryBreakdown(container, props) {
  const entries = Object.entries(props.registeredDietary || {});
  if (!entries.length && !props.registeredAttendees) return;

  const scope = props.registeredAttendees ? ` (${props.registeredAttendees} registered)` : "";
  if (!entries.length) {
    addMeta(container, `Dietary${scope}`, "none declared");
    return;
  }

  const line = document.createElement("div");
  line.className = "fc-event-popover-meta-line";
  const label = document.createElement("span");
  label.className = "fc-event-popover-meta-label";
  label.textContent = `Dietary${scope}:`;
  line.appendChild(label);
  const list = document.createElement("ul");
  list.className = "fc-event-popover-list";
  entries.forEach(([choice, count]) => {
    const item = document.createElement("li");
    item.textContent = `${count} × ${choice}`;
    list.appendChild(item);
  });
  line.appendChild(list);
  container.appendChild(line);
}

function addMeta(container, label, value) {
  const line = document.createElement("div");
  line.className = "fc-event-popover-meta-line";
  // GR-4096: built from text nodes, because the Event Lead label is typed by
  // an org admin and must never be parsed as markup.
  const labelSpan = document.createElement("span");
  labelSpan.className = "fc-event-popover-meta-label";
  labelSpan.textContent = `${label}:`;
  line.append(labelSpan, ` ${String(value)}`);
  container.appendChild(line);
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function humanize(str) {
  if (!str) return "";
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
